import React, { useEffect, useState } from "react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

// Auto-sliding poster carousel for the "Kelas Online" landing card.
// Click a poster to open it in the parent lightbox.
export default function OnlinePosterSlider({ posters, onOpen, ms, interval = 4000, contain = false }) {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!api) return undefined;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api]);

  useEffect(() => {
    if (!api || paused) return undefined;
    const id = setInterval(() => api.scrollNext(), interval);
    return () => clearInterval(id);
  }, [api, paused, interval]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      data-testid="online-poster-slider"
    >
      <Carousel setApi={setApi} opts={{ loop: true, align: "start" }} className="w-full">
        <CarouselContent className="-ml-0">
          {posters.map((p, i) => (
            <CarouselItem key={p.src} className="pl-0">
              <button
                type="button"
                onClick={() => onOpen(p.src)}
                className="pmm-cinematic group relative block w-full overflow-hidden rounded-2xl border-2 border-yellow-400/70 shadow-xl"
                data-testid={`online-poster-${i}`}
              >
                <img
                  src={p.src}
                  alt={ms ? p.altMs : p.altEn}
                  className={`aspect-square h-auto w-full transition-transform duration-700 group-hover:scale-105 ${contain ? "bg-navy-950 object-contain" : "object-cover"}`}
                  loading="lazy"
                />
                <span className="pointer-events-none absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                  <ZoomIn className="h-4 w-4" />
                </span>
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <button
        type="button"
        onClick={() => api?.scrollPrev()}
        aria-label="Previous poster"
        className="absolute left-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-blue-800 shadow-lg transition-colors hover:bg-blue-800 hover:text-white"
        data-testid="online-poster-prev"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => api?.scrollNext()}
        aria-label="Next poster"
        className="absolute right-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-blue-800 shadow-lg transition-colors hover:bg-blue-800 hover:text-white"
        data-testid="online-poster-next"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div className="mt-3 flex items-center justify-center gap-2" data-testid="online-poster-dots">
        {posters.map((p, i) => (
          <button
            key={p.src}
            type="button"
            onClick={() => api?.scrollTo(i)}
            aria-label={`Poster ${i + 1}`}
            className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${
              current === i ? "w-8 bg-blue-800" : "w-2.5 bg-blue-800/25 hover:bg-blue-800/50"
            }`}
            data-testid={`online-poster-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
